import type { RouterConfig } from "@nuxt/schema";
import { nextTick } from "vue";
import { START_LOCATION } from "vue-router";
import { useNuxtApp } from "#app";

// Replaces Nuxt's default scrollBehavior, which defers the scroll-to-top
// until `page:finish` (i.e. until the new page's async data resolves). On
// slow GraphQL responses that let the user scroll the old page and then get
// smooth-scrolled back to top seconds later.
export default <RouterConfig>{
  scrollBehavior(to, from, savedPosition) {
    // Initial page load / hydration: never touch scroll. The browser owns it
    // (top by default, restored position on reload/back-into-site). Without
    // this guard, a user who scrolls while the page is still hydrating gets
    // yanked back to the top.
    if (from === START_LOCATION) {
      return false;
    }

    // Back/forward: restore position after the new content renders,
    // otherwise the document isn't tall enough to scroll to it yet.
    if (savedPosition) {
      const nuxtApp = useNuxtApp();
      return new Promise((resolve) => {
        nuxtApp.hooks.hookOnce("page:finish", () => {
          nextTick(() => resolve({ ...savedPosition, behavior: "instant" }));
        });
      });
    }

    // In-page anchors keep smooth scrolling (html has scroll-smooth).
    if (to.hash) {
      return { el: to.hash, behavior: "smooth" };
    }

    // Same path (query/facet changes): keep the current position.
    if (to.path === from.path) {
      return false;
    }

    // Route change: jump to top immediately, before data resolves, like a
    // real page load. behavior "instant" is required to override the global
    // CSS scroll-behavior: smooth; "auto" would still animate.
    return { left: 0, top: 0, behavior: "instant" };
  },
};
